import { useState } from "react";
import { getPools, type PoolSummary } from "@/api/mockData";
import { t, formatRub, type Lang } from "@/lib/i18n";

interface PoolListProps {
  lang: Lang;
  onSelectPool: (id: string) => void;
}

type Filter = "all" | "mining" | "collecting" | "completed";

export function PoolList({ lang, onSelectPool }: PoolListProps) {
  const [filter, setFilter] = useState<Filter>("all");
  const pools = getPools();

  const filtered = filter === "all" ? pools : pools.filter((p) => p.status === filter);

  const filters: { key: Filter; label: string }[] = [
    { key: "all", label: t("pools.all", lang) },
    { key: "mining", label: t("pools.active", lang) },
    { key: "collecting", label: t("pools.collecting", lang) },
    { key: "completed", label: t("pools.completed", lang) },
  ];

  return (
    <div className="space-y-7 animate-fade-in">
      {/* Header */}
      <div className="pt-3">
        <h1 className="font-display text-3xl font-black tracking-tight">{t("pools.title", lang)}</h1>
        <p className="text-muted-foreground text-sm mt-1.5">{t("pools.subtitle", lang)}</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-xl text-[11px] font-semibold whitespace-nowrap transition-all duration-300 ${
              filter === f.key
                ? "bg-white/8 text-white border border-white/15"
                : "bg-secondary/40 text-muted-foreground hover:text-foreground border border-transparent"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Pool Cards */}
      <div className="space-y-3">
        {filtered.map((pool, i) => (
          <PoolCard
            key={pool.id}
            pool={pool}
            lang={lang}
            delay={i}
            onClick={() => onSelectPool(pool.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PoolCard({
  pool,
  lang,
  delay,
  onClick,
}: {
  pool: PoolSummary;
  lang: Lang;
  delay: number;
  onClick: () => void;
}) {
  const progress = (pool.collectedAmount / pool.targetAmount) * 100;

  return (
    <button
      onClick={onClick}
      className="w-full text-left glass-card rounded-2xl overflow-hidden animate-fade-in-up group"
      style={{ animationDelay: `${delay * 60}ms` }}
    >
      {/* Top color bar — subtle white gradient */}
      <div className="h-[2px]" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.03), transparent)' }} />

      <div className="p-4 space-y-3.5">
        <div className="flex items-start gap-3.5">
          {/* GPU Icon — more refined */}
          <div className="relative shrink-0">
            <div
              className="w-12 h-12 rounded-xl flex flex-col items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#888888', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <span className="text-[10px] font-display font-black leading-none">GPU</span>
              <span className="text-[10px] font-mono-num font-bold leading-none mt-0.5 opacity-70">×{pool.gpuCount}</span>
            </div>
            {pool.status === "mining" && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-white/50 border-2 border-background" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[14px] font-bold truncate">{pool.name}</h3>
              <StatusPill status={pool.status} lang={lang} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{pool.gpuModel}</p>
          </div>
        </div>

        {/* Stats row — subtle background */}
        <div className="grid grid-cols-3 gap-1.5">
          <MiniStat label={t("pools.hashrate", lang)} value={pool.hashrate} />
          <MiniStat label={t("pools.yield", lang)} value={`${pool.monthlyYield}%`} accent />
          <MiniStat
            label={t("pools.participants", lang)}
            value={String(pool.participantCount)}
          />
        </div>

        {/* Progress */}
        {pool.status === "collecting" && (
          <div>
            <div className="flex justify-between text-[10px] mb-1.5">
              <span className="text-muted-foreground">{t("pools.collected", lang)}</span>
              <span className="font-mono-num font-semibold text-foreground/80">
                {formatRub(pool.collectedAmount)} <span className="text-muted-foreground font-normal">/ {formatRub(pool.targetAmount)}</span>
              </span>
            </div>
            <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
              <div
                className="progress-bar h-full transition-all duration-1000"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {pool.status === "mining" && (
          <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
            <span className="text-[11px] text-muted-foreground/70">{t("pool.dailyReward", lang)}</span>
            <span className="font-mono-num text-[13px] font-bold text-primary bg-primary/8 px-2.5 py-1 rounded-lg border border-primary/10">
              +{pool.dailyRewardGNK} GNK
            </span>
          </div>
        )}

        {pool.status === "collecting" && (
          <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
            <span className="text-[11px] text-muted-foreground/70">
              {t("pools.target", lang)}: {formatRub(pool.targetAmount)}
            </span>
            <span className="cta-join-pill">
              {t("pools.join", lang)}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

function MiniStat({ label, value }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="text-center rounded-lg py-2.5 px-1.5 bg-secondary/25">
      <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium">{label}</p>
      <p className="text-[13px] font-mono-num font-bold mt-1 text-foreground/75">{value}</p>
    </div>
  );
}

function StatusPill({ status, lang }: { status: string; lang: Lang }) {
  const labels: Record<string, string> = {
    mining: t("pools.status.mining", lang),
    collecting: t("pools.status.collecting", lang),
    completed: t("pools.status.completed", lang),
  };

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.08] text-white/80">
      <span className={`w-1.5 h-1.5 rounded-full ${status === "mining" ? "mining-beacon" : "bg-white/40"}`} />
      {labels[status] ?? status}
    </span>
  );
}
